import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router';

import { AccountPermission } from 'modules/permissions/constants';
import { billingRoutesConfig } from 'domains/account/routes';
import { useAccountStatusTransition } from 'domains/account/hooks/useAccountStatusTransation';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useMyBundles } from 'modules/billing/hooks/useMyBundles';

import { PostTopUpLocationState } from '../types';

const { billing, cardPaymentSuccess } = billingRoutesConfig;

export const useStatusTransitionDialog = () => {
  const { hasFreemiumToPremiumTransition } = useAccountStatusTransition();

  const navigate = useNavigate();
  const location = useLocation();
  const origin = (location?.state as PostTopUpLocationState)?.origin;

  const hasStatusAccess = useCheckAccountPermission({
    permission: AccountPermission.AccountStatus,
  });

  const { data: myBundles } = useMyBundles();
  const isLoaded = myBundles !== undefined;

  const isSubscribed = (myBundles?.length ?? 0) > 0;

  const shouldShowDialog = useMemo(() => {
    const isRedirectedFromSuccessTopUp =
      origin === cardPaymentSuccess.path || origin === billing.path;

    return (
      (hasFreemiumToPremiumTransition || (isSubscribed && isLoaded)) &&
      isRedirectedFromSuccessTopUp &&
      hasStatusAccess
    );
  }, [
    hasFreemiumToPremiumTransition,
    isSubscribed,
    origin,
    hasStatusAccess,
    isLoaded,
  ]);

  const { isOpened, onClose, onOpen } = useDialog(shouldShowDialog);

  const handleClose = useCallback(() => {
    navigate(location?.pathname, { replace: true, state: {} });
    onClose();
  }, [onClose, navigate, location?.pathname]);

  useEffect(() => {
    if (shouldShowDialog) {
      onOpen();
    }
  }, [onOpen, shouldShowDialog]);

  return { isOpened, onClose: handleClose, onOpen };
};
